import type { SxmBusyError } from '@shared/types.ts';
import { Focusable } from '@/ui/Focusable.tsx';
import { Modal } from '@/ui/Modal.tsx';

/**
 * SiriusXM allows one channel at a time and nobody is ever kicked off, so a
 * second listener on a different channel gets this instead of a stalled
 * player: what's on, who's listening, and a one-click way to join it.
 *
 * Default focus is on "Listen" — it is the first [data-nav] inside the modal,
 * which is where <Modal> puts focus, so the common case is one press of OK.
 * Two buttons only; three is one too many on a remote.
 */
export function SourceBusyDialog({
  active, onListen, onCancel,
}: {
  active: SxmBusyError['active'];
  onListen(): void;
  onCancel(): void;
}) {
  const who = active.listeners.length
    ? `${active.listeners.join(' and ')} ${active.listeners.length > 1 ? 'are' : 'is'} listening to`
    : 'Already playing';

  return (
    <Modal onClose={onCancel} labelledBy="busy-title">
      <h2 id="busy-title">SiriusXM is already in use</h2>

      <div className="active">
        {active.logo && <img src={active.logo} alt="" />}
        <div>
          <div className="muted">{who}</div>
          <div className="big">{active.channelName}</div>
          <div className="muted">Ch. {active.channelNumber}</div>
          {active.nowPlaying && (
            <div className="track">
              ♪ {active.nowPlaying.title}
              {active.nowPlaying.artist ? ` — ${active.nowPlaying.artist}` : ''}
            </div>
          )}
        </div>
      </div>

      <div className="actions">
        <Focusable id="busy-listen" onSelect={onListen} className="btn primary">
          Listen to {active.channelName}
        </Focusable>
        <Focusable id="busy-cancel" onSelect={onCancel} className="btn">
          Cancel
        </Focusable>
      </div>
    </Modal>
  );
}
